import { makeObservable, observable } from 'mobx'

import {
  IGeoJson2DGeographicCoordinates,
  ISitemapItem,
  ISitemapItemShapeCoordinates,
  SitemapItemShapeType,
} from '~/client/graph'
import MapViewItemType from '~/client/src/shared/enums/MapViewItemType'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import IHierarchyParent from '~/client/src/shared/types/IHierarchyParent'
import Guard from '~/client/src/shared/utils/Guard'

import CircleShapeCoordinates from '../components/SitemapHelpers/models/CircleShapeCoordinates'
import PolyLineShapeCoordinates from '../components/SitemapHelpers/models/PolyLineShapeCoordinates'
import RectangleShapeCoordinates from '../components/SitemapHelpers/models/RectangleShapeCoordinates'
import { areObjectsEqual, copyObject } from '../utils/util'
import BaseModel from './BaseModel'
import LocationBase from './LocationObjects/LocationBase'

export type ShapeCoordinates =
  | PolyLineShapeCoordinates
  | CircleShapeCoordinates
  | RectangleShapeCoordinates

export default class SitemapItem
  extends BaseModel<ISitemapItem>
  implements ISitemapItem
{
  public static fromDto(dto: ISitemapItem) {
    return new SitemapItem(
      dto.id,
      dto.name,
      dto.color,
      dto.type as MapViewItemType,
      dto.iconName as MapViewLocationIcon,
      dto.projectId,
      dto.assignedId,
      dto.parent as IHierarchyParent,
      dto.createdAt,
      dto.updatedAt,
      dto.coordinates,
      dto.shapeCoordinates,
    )
  }

  @observable public name: string = null
  @observable public color: string = null
  @observable public type: MapViewItemType = null
  @observable public iconName: MapViewLocationIcon = null
  public projectId: string
  @observable public coordinates?: IGeoJson2DGeographicCoordinates = null
  @observable public assignedId?: string = null
  @observable public parent: IHierarchyParent = null
  @observable public shapeCoordinates?: ShapeCoordinates = null

  public constructor(
    id: string,
    name: string,
    color: string,
    type: MapViewItemType,
    iconName: MapViewLocationIcon,
    projectId: string,
    assignedId?: string,
    parent?: IHierarchyParent,
    createdAt: number = 0,
    updatedAt: number = 0,
    coordinates?: IGeoJson2DGeographicCoordinates,
    shapeCoordinates?: ISitemapItemShapeCoordinates,
  ) {
    super(id)
    this.name = name
    this.color = color
    this.type = type
    this.iconName = iconName
    this.projectId = projectId
    this.assignedId = assignedId
    this.parent = parent
    this.setCreatedAt(createdAt)
    this.setUpdatedAt(updatedAt)
    this.coordinates = coordinates
    if (shapeCoordinates) {
      switch (shapeCoordinates?.type) {
        case SitemapItemShapeType.Polyline:
          this.shapeCoordinates = new PolyLineShapeCoordinates(
            shapeCoordinates.parts?.length
              ? shapeCoordinates.parts.map(part => ({
                  rings: part.rings as IGeoJson2DGeographicCoordinates[][],
                }))
              : [{ rings: [shapeCoordinates.coordinates ?? []] }],
            shapeCoordinates.isClosed,
          )
          break
        case SitemapItemShapeType.Circle:
          this.shapeCoordinates = new CircleShapeCoordinates(
            shapeCoordinates.coordinates,
            shapeCoordinates.isClosed,
            shapeCoordinates.divisionEndAngle,
            shapeCoordinates.divisionStartAngle,
          )
          break
        case SitemapItemShapeType.Rectangle:
          this.shapeCoordinates = new RectangleShapeCoordinates(
            shapeCoordinates.coordinates,
            shapeCoordinates.isClosed,
          )
          break
      }
    }
    makeObservable(this)
    Guard.requireAll({ name, projectId, color })
  }

  public isEqual(other: SitemapItem) {
    return (
      this.id === other.id &&
      this.name === other.name &&
      this.color === other.color &&
      this.projectId === other.projectId &&
      this.assignedId === other.assignedId &&
      areObjectsEqual(this.coordinates || {}, other.coordinates || {}) &&
      ((!this.shapeCoordinates && !other.shapeCoordinates) ||
        this.shapeCoordinates?.isEqual(other.shapeCoordinates)) &&
      areObjectsEqual(this.parent || {}, other.parent || {})
    )
  }

  /**
   * By id alone. A pin names its location by assignedId; the legacy assignedTo kind is
   * neither read nor sent (STX1-1355).
   */
  public isAssignedTo(dto: LocationBase): boolean {
    return !!dto && !!this.assignedId && this.assignedId === dto.id
  }

  public assign(dto: LocationBase) {
    this.assignedId = dto.id
  }

  public get isAssigned(): boolean {
    return !!this.assignedId
  }

  public copy(): SitemapItem {
    return new SitemapItem(
      this.id,
      this.name,
      this.color,
      this.type,
      this.iconName,
      this.projectId,
      this.assignedId,
      this.parent && copyObject(this.parent),
      this.createdAt,
      this.updatedAt,
      this.coordinates && copyObject(this.coordinates),
      this.shapeCoordinates && this.shapeCoordinates.copy(),
    )
  }
}
